import { callEndpoint } from '../backend';
import { get, remove } from '../local-storage';
import {
  postMemberSettings,
  updateMemberSettings,
} from '../store/modules/app-controls';
import { getStore } from '../store';
import captureSpecificError from '../sentry';

export const requestValidationOfClientTokenViaEmailToken = (email) =>
  callEndpoint(
    callEndpoint.endpoints.requestValidationOfClientTokenViaEmailToken,
    { email },
  );

export const validateClientTokenViaEmailToken = (emailToken) => {
  callEndpoint(callEndpoint.endpoints.validateClientTokenViaEmailToken, {
    emailToken,
  });
};

const convertUnitsConfig = ({ unitsConfig, defaultUnitsConfig }) => ({
  measurementSystem:
    unitsConfig?.system?.options?.find((option) => option.active === true)
      ?.id ?? 'custom',
  coordinate:
    unitsConfig?.coordinate?.options?.find((option) => option.active === true)
      ?.id ?? defaultUnitsConfig.coordinate,
  distance:
    unitsConfig?.distance?.options?.find((option) => option.active === true)
      ?.id ?? defaultUnitsConfig.distance,
  speed:
    unitsConfig?.speed?.options?.find((option) => option.active === true)?.id ??
    defaultUnitsConfig.speed,
  swellHeight:
    unitsConfig?.swellHeight?.options?.find((option) => option.active === true)
      ?.id ?? defaultUnitsConfig.swellHeight,
  temperature:
    unitsConfig?.temperature?.options?.find((option) => option.active === true)
      ?.id ?? defaultUnitsConfig.temperature,
  tideHeight:
    unitsConfig?.tideHeight?.options?.find((option) => option.active === true)
      ?.id ?? defaultUnitsConfig.tideHeight,
  windSpeed:
    unitsConfig?.windSpeed?.options?.find((option) => option.active === true)
      ?.id ?? defaultUnitsConfig.windSpeed,
});

const convertWeatherLayoutConfig = ({ weatherLayoutConfig }) =>
  weatherLayoutConfig.map((config) => ({
    id: config.id,
    order: config.order,
    showGraph: config.showGraph,
    active: config.active,
    label: config.label,
  }));

const convertWeatherConfig = ({ weatherConfig }) => ({
  weatherOnStartup: weatherConfig.weatherOnStartup,
  weatherOnMapCenter: weatherConfig.useMapCenter,
});

export const initialize = async () => {
  const member = getStore().getState().backendService.currentMember;

  // Early return if guest user
  if (!member) {
    return;
  }

  let memberSettings = null;
  try {
    memberSettings = await callEndpoint(
      callEndpoint.endpoints.getMemberSettings,
    );
  } catch (err) {
    captureSpecificError(
      err,
      'containers/app-main-client/src/services/user/index.js',
    );
  }

  if (memberSettings) {
    // Update redux with all their settings
    getStore().dispatch(updateMemberSettings(memberSettings.selectedSettings));
  } else {
    const usersUnitsConfig = await get('userService', 'unitsConfig');
    const weatherSettings = await get('weather', 'settings');
    const tripsConfig = await get('userService', 'tripsConfig');

    const defaultMemberSettings =
      getStore().getState().appControls.memberSettings;

    const newMemberSettings = {
      ...defaultMemberSettings,
    };

    if (usersUnitsConfig) {
      newMemberSettings.unitsConfig = convertUnitsConfig({
        unitsConfig: usersUnitsConfig,
        defaultUnitsConfig: defaultMemberSettings.unitsConfig,
      });
    }
    if (tripsConfig) newMemberSettings.tripsConfig = tripsConfig;
    if (weatherSettings) {
      newMemberSettings.weatherConfig = convertWeatherConfig({
        weatherConfig: weatherSettings.weatherConfig,
      });
      newMemberSettings.weatherLayoutConfig = convertWeatherLayoutConfig({
        weatherLayoutConfig: weatherSettings.weatherLayoutConfig,
      });
    }

    try {
      await getStore().dispatch(postMemberSettings(newMemberSettings)).unwrap();

      // If the endpoint call was successful, then remove localStorage settings
      // We no longer want to use localStorage
      remove('userService', 'unitsConfig');
      remove('weather', 'settings');
      remove('userService', 'tripsConfig');
    } catch (err) {
      captureSpecificError(
        err,
        'containers/app-main-client/src/services/user/index.js',
      );
    }
  }
};
